// AI module i18n - English
export const aiEn = {
  ai: {
    chat: {
      title: 'AI Assistant',
      description: 'Chat with the AI assistant to get help with your recipes and shopping',
      context: 'Context',
      showContextConfig: 'Show context configuration',
      clearMessages: 'Clear messages',
      startConversation: 'Start a conversation with AI...',
      thinking: 'AI is thinking...',
      placeholder: 'Ask AI anything...',
      send: 'Send message',
      sendHint: 'Ctrl+Enter to send',
      includeContainerData: {
        tooltip: 'Include list data in context',
        label: 'Include data'
      },
      templateMessages: {
        dinnerIdea: {
          label: 'Dinner idea',
          prompt: 'Suggest a simple dinner idea for a family.',
        },
        shoppingHelp: {
          label: 'Shopping help',
          prompt: 'What staples should always be on a family shopping list?',
        },
        mealPlan: {
          label: 'Meal plan',
          prompt: 'Propose a simple meal plan for the next 3 days.',
        },
      },
      history: {
        title: 'History',
        empty: 'No history',
        restore: 'Restore',
        openHistory: 'Open history',
      },
    },
    model: {
      selectPlaceholder: 'Select model',
      selectTooltip: 'Select AI model',
    },
    models: {
      loadError: 'Failed to load AI models',
    },
    settings: {
      loadError: 'Failed to load AI settings',
      title: 'AI Settings',
      description: 'Configure AI model, API token, and usage limits',
      defaultModel: {
        label: 'Default Model',
        subtitle: 'Select the default AI model to use for chat and suggestions',
        placeholder: 'Select a model',
      },
      apiToken: {
        label: 'API Token',
        subtitle: 'Use your own OpenRouter API token for unlimited usage (optional)',
        placeholder: 'Enter your OpenRouter API token',
        save: 'Save Token',
        remove: 'Remove Token',
        configured: 'Token configured',
        encrypted: 'Token is encrypted and stored securely',
        confirmRemove: {
          title: 'Remove API Token',
          description: 'Are you sure you want to remove your API token? You will need to configure it again to use AI features with your own token.',
        },
      },
      usage: {
        label: 'Usage & Limits',
        subtitle: 'Current usage statistics and limits for this month',
        tokens: 'Tokens',
        cost: 'Cost',
        unlimited: 'Unlimited',
        noLimit: 'No limit',
        unavailable: 'Unavailable',
        ownTokenNote: 'You are using your own API token. Usage is unlimited.',
        systemTokenNote: 'You are using the system token. Limits apply.',
      },
      modelUpdated: 'Model updated successfully',
      tokenSaved: 'API token saved successfully',
      tokenRemoved: 'API token removed successfully',
      tokenRequired: 'API token is required',
      tokenSaveError: 'Failed to save API token',
      tokenRemoveError: 'Failed to remove API token',
    },
    context: {
      fields: 'Context Fields',
      description: 'Select which fields to include when sending data to AI',
      fieldNames: {
        name: 'Name',
        category: 'Category',
        quantity: 'Quantity',
        unit: 'Unit',
        notes: 'Notes',
      },
    },
    cost: {
      tokens: 'Tokens:',
      cost: 'Cost:',
      in: 'in',
      out: 'out',
    },
    premium: {
      title: 'Premium Feature',
      description: 'AI settings are available for Premium users',
      ownTokenOption: 'Alternatively, configure your own OpenRouter API token in Settings to use AI features for free.',
      modelSelectorDisabled: 'Model selection requires Premium',
      contextConfigDisabled: 'Context configuration requires Premium',
    },
    actions: {
      error: 'Failed to execute AI action',
      invalidData: 'Invalid data received from AI',
    },
    history: {
      title: 'AI History',
      empty: 'No history yet',
      restore: 'Restore conversation',
      delete: 'Delete',
      deleteAll: 'Delete all',
      confirmDelete: 'Are you sure you want to delete this conversation?',
      confirmDeleteAll: 'Are you sure you want to delete all conversations? This action cannot be undone.',
      clearFilters: 'Clear filters',
      refresh: 'Refresh',
      filters: {
        operationType: 'Operation type',
        search: 'Search',
        all: 'All',
      },
      operationTypes: {
        chat: 'Chat',
        classify: 'Classify',
        analyze: 'Analyze',
        generate: 'Generate',
      },
      details: 'Details',
      back: 'Back',
      model: 'Model',
      provider: 'Provider',
      tokens: 'Tokens',
      cost: 'Cost',
      duration: 'Duration',
      createdAt: 'Created at',
      operationType: 'Operation type',
      preview: 'Preview',
      noPreview: 'No preview available',
      resumeChat: 'Resume chat',
      viewAll: 'View all',
    },
  },
}

// AI module i18n - Polish
export const aiPl = {
  ai: {
    chat: {
      title: 'Asystent AI',
      description: 'Czatuj z asystentem AI, aby uzyskać pomoc z przepisami i zakupami',
      context: 'Kontekst',
      showContextConfig: 'Pokaż konfigurację kontekstu',
      clearMessages: 'Wyczyść wiadomości',
      startConversation: 'Rozpocznij rozmowę z AI...',
      thinking: 'AI myśli...',
      placeholder: 'Zapytaj AI o cokolwiek...',
      send: 'Wyślij wiadomość',
      sendHint: 'Ctrl+Enter aby wysłać',
      includeContainerData: {
        tooltip: 'Dołącz dane listy do kontekstu',
        label: 'Dołącz dane'
      },
      templateMessages: {
        dinnerIdea: {
          label: 'Pomysł na obiad',
          prompt: 'Zaproponuj prosty pomysł na rodzinny obiad.',
        },
        shoppingHelp: {
          label: 'Pomoc w zakupach',
          prompt: 'Jakie podstawowe produkty powinny zawsze być na rodzinnej liście zakupów?',
        },
        mealPlan: {
          label: 'Plan posiłków',
          prompt: 'Zaproponuj prosty plan posiłków na najbliższe 3 dni.',
        },
      },
      history: {
        title: 'Historia',
        empty: 'Brak historii',
        restore: 'Przywróć',
        openHistory: 'Otwórz historię',
      },
    },
    model: {
      selectPlaceholder: 'Wybierz model',
      selectTooltip: 'Wybierz model AI',
    },
    models: {
      loadError: 'Nie udało się załadować modeli AI',
    },
    settings: {
      loadError: 'Nie udało się załadować ustawień AI',
      title: 'Ustawienia AI',
      description: 'Skonfiguruj model AI, token API i limity użycia',
      defaultModel: {
        label: 'Domyślny Model',
        subtitle: 'Wybierz domyślny model AI do użycia w czacie i sugestiach',
        placeholder: 'Wybierz model',
      },
      apiToken: {
        label: 'Token API',
        subtitle: 'Użyj własnego tokena OpenRouter API dla nieograniczonego użycia (opcjonalnie)',
        placeholder: 'Wprowadź swój token OpenRouter API',
        save: 'Zapisz Token',
        remove: 'Usuń Token',
        configured: 'Token skonfigurowany',
        encrypted: 'Token jest szyfrowany i przechowywany bezpiecznie',
        confirmRemove: {
          title: 'Usuń Token API',
          description: 'Czy na pewno chcesz usunąć swój token API? Będziesz musiał skonfigurować go ponownie, aby korzystać z funkcji AI z własnym tokenem.',
        },
      },
      usage: {
        label: 'Użycie i Limity',
        subtitle: 'Aktualne statystyki użycia i limity dla tego miesiąca',
        tokens: 'Tokeny',
        cost: 'Koszt',
        unlimited: 'Bez limitu',
        noLimit: 'Brak limitu',
        unavailable: 'Niedostępne',
        ownTokenNote: 'Używasz własnego tokena API. Użycie jest nieograniczone.',
        systemTokenNote: 'Używasz tokena systemowego. Obowiązują limity.',
      },
      modelUpdated: 'Model został zaktualizowany',
      tokenSaved: 'Token API został zapisany',
      tokenRemoved: 'Token API został usunięty',
      tokenRequired: 'Token API jest wymagany',
      tokenSaveError: 'Nie udało się zapisać tokena API',
      tokenRemoveError: 'Nie udało się usunąć tokena API',
    },
    context: {
      fields: 'Pola kontekstu',
      description: 'Wybierz które pola uwzględnić podczas wysyłania danych do AI',
      fieldNames: {
        name: 'Nazwa',
        category: 'Kategoria',
        quantity: 'Ilość',
        unit: 'Jednostka',
        notes: 'Notatki',
      },
    },
    cost: {
      tokens: 'Tokeny:',
      cost: 'Koszt:',
      in: 'wejście',
      out: 'wyjście',
    },
    premium: {
      title: 'Funkcja Premium',
      description: 'Ustawienia AI są dostępne dla użytkowników Premium',
      ownTokenOption: 'Alternatywnie, skonfiguruj własny token API OpenRouter w Ustawieniach, aby korzystać z funkcji AI za darmo.',
      modelSelectorDisabled: 'Wybór modelu wymaga Premium',
      contextConfigDisabled: 'Konfiguracja kontekstu wymaga Premium',
    },
    actions: {
      error: 'Nie udało się wykonać akcji AI',
      invalidData: 'Otrzymano nieprawidłowe dane od AI',
    },
    history: {
      title: 'Historia AI',
      empty: 'Brak historii',
      restore: 'Przywróć konwersację',
      delete: 'Usuń',
      deleteAll: 'Usuń wszystkie',
      confirmDelete: 'Czy na pewno chcesz usunąć tę konwersację?',
      confirmDeleteAll: 'Czy na pewno chcesz usunąć wszystkie konwersacje? Ta akcja nie może być cofnięta.',
      clearFilters: 'Wyczyść filtry',
      refresh: 'Odśwież',
      filters: {
        operationType: 'Typ operacji',
        search: 'Wyszukaj',
        all: 'Wszystkie',
      },
      operationTypes: {
        chat: 'Czat',
        classify: 'Klasyfikuj',
        analyze: 'Analizuj',
        generate: 'Generuj',
      },
      details: 'Szczegóły',
      back: 'Wróć',
      model: 'Model',
      provider: 'Dostawca',
      tokens: 'Tokeny',
      cost: 'Koszt',
      duration: 'Czas trwania',
      createdAt: 'Utworzono',
      operationType: 'Typ operacji',
      preview: 'Podgląd',
      noPreview: 'Brak podglądu',
      resumeChat: 'Wznów czat',
      viewAll: 'Zobacz wszystkie',
    },
  },
}
